/**
 * Classificação de atendimento da localidade (interna x externa).
 *
 * Fonte de verdade: o custom field `atendimento` do site no NetBox, que vem
 * junto do objeto de localidade carregado no estado global. Quando a
 * localidade não tem o campo preenchido, cai na lista manual cadastrada em
 * Rotas › Localidades internas.
 *
 * Localidade externa faz o chamado ser sugerido como atendimento externo.
 * Sem classificação em nenhuma das duas fontes, nada é sugerido.
 */

const CUSTOM_FIELD = "atendimento";

/**
 * O NetBox devolve o custom field como string ("interna") ou como objeto
 * ({value, label}). Aceita variações de caixa, acento e gênero.
 */
export function normalizarAtendimento(valor) {
  const bruto = (valor && typeof valor === "object")
    ? (valor.value ?? valor.label ?? "")
    : valor;

  const texto = String(bruto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

  if (!texto) return null;
  if (texto.startsWith("intern")) return "interna";
  if (texto.startsWith("extern")) return "externa";
  return null;
}

/** Lê a classificação direto do custom field da localidade. */
export function classificacaoNetbox(localidade) {
  return normalizarAtendimento(localidade?.custom_fields?.[CUSTOM_FIELD]);
}

/**
 * Classificação final da localidade.
 *
 * @param {object} localidade objeto do NetBox (estado global $localidades)
 * @param {number[]} internasManuais ids marcados em Rotas › Localidades internas
 * @returns {'interna'|'externa'|null}
 */
export function classificarLocalidade(localidade, internasManuais = []) {
  if (!localidade) return null;

  const doNetbox = classificacaoNetbox(localidade);
  if (doNetbox) return doNetbox;

  const lista = Array.isArray(internasManuais) ? internasManuais.map(Number) : [];
  if (lista.length === 0) return null;

  const id = Number(localidade?.id ?? localidade);
  if (!Number.isFinite(id)) return null;
  return lista.includes(id) ? "interna" : "externa";
}

/** true somente quando a localidade está classificada como externa. */
export function ehAtendimentoExterno(localidade, internasManuais = []) {
  return classificarLocalidade(localidade, internasManuais) === "externa";
}

export default {
  normalizarAtendimento,
  classificacaoNetbox,
  classificarLocalidade,
  ehAtendimentoExterno,
};
